import type { BadgeKey } from './navModel'
import { useLighthouseAdmin } from '../hooks/useLighthouseAdmin'
import { useGovernanceFinding } from '../hooks/useGovernance'
import { useDeadLetterStats } from '../hooks/useQueues'
import { useSignalSummary } from '../hooks/useSignals'
import { useRequestBackstopSummary } from '../hooks/useRequestBackstop'
import { signalInboxBadge } from '../components/signals/signalFormat'
import { requestBackstopBadge } from '../components/requests/requestBackstopFormat'
import { SLOW } from '../utils/polling'

export type NavBadges = Partial<Record<BadgeKey, number>>

/** Per badge, why its count could not be measured — rendered as a muted dash, never as nothing. */
export type NavUnmeasuredBadges = Partial<Record<BadgeKey, string>>

/**
 * Live counts for the rail badges. A badge means "things need your attention",
 * so every count here is one a person can act on, and an unmeasured count is
 * never shown as a problem.
 *
 * `governanceQueue` is the OWNERSHIP-GAP count alone: reports with no
 * accountable owner. The other governance groups are deliberately left out
 * (ObservatoryGovernanceModels.cs): lapsed grants confer nothing, so they are
 * housekeeping rather than exposure; broad reach carries no verdict; and
 * inactivity is the weakest finding there and must never be the headline.
 * It is fetched ONLY when the server's own answer is `admin`
 * (hooks/useLighthouseAdmin.ts, the job catalogue's `permission`). The endpoint is
 * LighthouseAdmin at class level, so a non-admin never 403-polls it, and on
 * `unknown` the badge waits rather than guessing. The query key is the page's and
 * the preview's (`['observatory', 'governance', 'ownership']`), so the three share
 * one request.
 *
 * `queues` is the dead-lettered message count over REACHABLE queues only.
 * `GET api/system/dead-letters` is bare [Authorize], so every caller may read it
 * and the badge needs no gate. A queue the server could not inspect still sends
 * messageCount 0 as a placeholder: it contributes nothing, and nothing is not
 * zero. When every queue is unavailable, or the payload is not a list, there is
 * no badge — never a count implying the queues are empty. SLOW here; the Queues
 * page polls the same cache entry faster.
 *
 * `signalInbox` is the count of OBSERVED open critical signals, and only while the
 * detector reports (`signalInboxBadge`, over `GET api/observatory/signals/summary`,
 * bare [Authorize], so no gate). An open critical the detector could not re-check
 * this pass is not counted — the badge claims only what was seen — and when the
 * store is unprovisioned, the detector is stale or has never run, or the summary
 * cannot be read, there is no count at all. That last case is NOT left quiet: it
 * is the one critical-toned row, and a missing badge there would read as
 * all-clear, so `useNavUnmeasuredBadges` below carries the reason and the rail
 * draws a muted dash. While the summary is loading there is neither. This hook
 * once fed the rail from the mock layer, which rendered attention-demanding counts
 * beside surfaces that did not exist; nothing here reads a fixture.
 *
 * `requestBackstop` is the count of open Cartographer requests on the backstop
 * (Cartographer doc 15 §7) — every one a request nobody else is going to move —
 * over `GET api/observatory/requests/summary`, `requestBackstopBadge`. That read is
 * LighthouseAdmin at class level, so like the Governance Queue it is fetched ONLY
 * on `admin`, and the page shares its query key. Unmeasured — the request table
 * not provisioned, the summary failed — is no badge: the row is not critical-toned,
 * and the page says why.
 *
 * `NavItem` hides a badge that is absent or zero, so an empty result leaves the
 * rail quiet — which, for a count that means "things need your attention", is the
 * only safe default. AppNavbar also withholds the badge from a row the server has
 * restricted.
 */
export function useNavBadges(): NavBadges {
  const gate = useLighthouseAdmin()
  const isAdmin = gate.state === 'admin'
  const ownership = useGovernanceFinding('ownership', { enabled: isAdmin })
  const backstop = useRequestBackstopSummary({ enabled: isAdmin })
  const deadLetters = useDeadLetterStats({ refetchInterval: SLOW })

  const badges: NavBadges = {}

  const gaps = isAdmin ? ownership.data?.ownershipGapCount : undefined
  if (gaps != null && gaps > 0) badges.governanceQueue = gaps

  const fellThrough = isAdmin ? requestBackstopBadge(backstop.data) : undefined
  if (fellThrough != null) badges.requestBackstop = fellThrough

  const queues = deadLetters.data
  const depth = Array.isArray(queues)
    ? queues.filter((q) => !q.unavailable).reduce((sum, q) => sum + q.messageCount, 0)
    : undefined
  if (depth != null && depth > 0) badges.queues = depth

  const signals = signalInboxBadge(useSignalSummary())
  if (signals.count) badges.signalInbox = signals.count

  return badges
}

/**
 * The badges whose count could not be measured, each with the reason. Only the
 * Signal Inbox has one: its row is the only critical-toned badge, so an unmeasured
 * count there must be visibly unmeasured (AppNavbar passes it to `NavItem` as
 * `badgeUnmeasured`). The other badges stay quiet when unmeasured, as they always
 * have — their tone never claimed an emergency.
 */
export function useNavUnmeasuredBadges(): NavUnmeasuredBadges {
  const signals = signalInboxBadge(useSignalSummary())
  const unmeasured: NavUnmeasuredBadges = {}
  if (signals.unmeasured) unmeasured.signalInbox = signals.unmeasured
  return unmeasured
}
